'use client'

import { CpfEmailInput } from '@/components/global/cpf-email.-input'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { bff_client } from '@/lib/apiClient'
import { validateCPF } from '@/utils/validate-cpf'
import { zodResolver } from '@hookform/resolvers/zod'
import { HTTPError } from 'ky'
import { ChevronLeft, LoaderCircle, Lock, Mail } from 'lucide-react'
import Link from 'next/link'
import { useEffect, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

const TOAST_DURATION = 10_000
const COUNTDOWN_SECONDS = 120
const COUNTDOWN_INTERVAL = 1000

// Regex patterns defined at top level for performance
const LETTER_PATTERN = /[a-zA-Z]/
const NUMBER_PATTERN = /\d/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const emailOrCpfValidation = z.string().superRefine((value, ctx) => {
  if (!value) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Campo obrigatório',
    })
    return
  }

  const looksLikeEmail = value.includes('@') || LETTER_PATTERN.test(value)

  if (looksLikeEmail) {
    if (!EMAIL_PATTERN.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Email inválido',
      })
    }
    return
  }

  if (NUMBER_PATTERN.test(value.charAt(0))) {
    if (!validateCPF(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CPF inválido',
      })
    }
    return
  }

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: 'Formato inválido. Digite um email ou CPF válido',
  })
})

export const forgotPassword = z.object({
  emailOrCpf: emailOrCpfValidation,
})

type ForgotPasswordType = z.infer<typeof forgotPassword>

const CPF_MASK_CHARS = /[.\-/]/g
const buildForgotPasswordPayload = (value: string) => {
  const isCpf = !(value.includes('@') || LETTER_PATTERN.test(value))
  if (isCpf) {
    return { cpf: value.replace(CPF_MASK_CHARS, '') }
  }
  return { email: value }
}

export default function ForgotPasswordForm() {
  const [isLoading, startTransition] = useTransition()
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [isCountdownActive, setIsCountdownActive] = useState(false)

  const form = useForm<ForgotPasswordType>({
    resolver: zodResolver(forgotPassword),
    mode: 'all',
    defaultValues: { emailOrCpf: '' },
  })

  function handleSubmitEmail(formData: ForgotPasswordType) {
    startTransition(async () => {
      try {
        const value = formData.emailOrCpf

        if (value.trim() === '') {
          return
        }

        await bff_client.post('user/forgot-password', {
          json: buildForgotPasswordPayload(value),
        })
        toast.success(
          'Pronto, você deve receber um email com um link para redefinir sua senha',
          {
            duration: TOAST_DURATION,
            position: 'top-right',
          }
        )

        setSecondsLeft(COUNTDOWN_SECONDS)
        setIsCountdownActive(true)
      } catch (error: unknown) {
        if (error instanceof HTTPError) {
          const response = await error.response.json()
          toast.error(response.message, {
            position: 'top-right',
            duration: TOAST_DURATION,
          })

          return
        }

        toast.error('Ocorreu um erro!', {
          position: 'top-right',
          duration: TOAST_DURATION,
        })
      }
    })
  }

  useEffect(() => {
    let interval: NodeJS.Timeout

    if (isCountdownActive && secondsLeft > 0) {
      interval = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 1) {
            setIsCountdownActive(false)
            return 0
          }
          return prev - 1
        })
      }, COUNTDOWN_INTERVAL)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [isCountdownActive, secondsLeft])

  const minutes = Math.floor(secondsLeft / 60)
  const seconds = secondsLeft % 60

  const emailOrCpfError = !!form.formState.errors.emailOrCpf?.message
  const canResend = !isCountdownActive && secondsLeft === 0

  const getButtonText = () => {
    if (isLoading) {
      return 'Enviando...'
    }
    if (canResend) {
      return 'Recuperar Acesso'
    }
    return 'Reenviar'
  }

  return (
    <div className="flex items-center justify-center">
      <div className="flex w-full max-w-md items-center justify-center">
        <div className="rounded-2xl border border-white/20 bg-white/80 p-6 shadow-lg backdrop-blur-xs lg:p-8">
          <div className="mx-auto space-y-6">
            <Link
              className="flex items-center gap-0.5 font-medium text-[#737373] text-sm"
              href="/"
            >
              <ChevronLeft className="size-4" />
              Voltar para página inicial
            </Link>

            <div className="space-y-3">
              <div>
                <h1 className="bg-linear-to-r from-slate-900 to-slate-700 bg-clip-text font-semibold text-2xl text-transparent lg:text-3xl">
                  Redefinição de senha!
                </h1>
                <p className="mt-1 text-left font-normal text-slate-600 text-sm lg:text-base">
                  Informe o seu e-mail e enviaremos um link para recuperação da
                  sua senha
                </p>
              </div>
            </div>

            <Form {...form}>
              <form
                className="space-y-5"
                noValidate
                onSubmit={form.handleSubmit(handleSubmitEmail)}
              >
                <FormField
                  control={form.control}
                  name="emailOrCpf"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                        <Mail className="h-4 w-4" />
                        E-mail
                      </FormLabel>
                      <FormControl>
                        <CpfEmailInput
                          className="h-11 rounded-xl border-2 border-slate-200 px-4 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100 data-[error=true]:border-red-500 data-[error=true]:focus:ring-red-100"
                          data-error={emailOrCpfError}
                          disabled={isLoading}
                          placeholder="Digite seu e-mail"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600 text-xs" />
                    </FormItem>
                  )}
                />

                {isCountdownActive && secondsLeft > 0 && (
                  <div className="text-center">
                    <span className="text-slate-600 text-sm">
                      Reenvie o E-mail novamente em{' '}
                      <strong className="text-primary-500">
                        {minutes.toString().padStart(2, '0')}:
                        {seconds.toString().padStart(2, '0')}
                      </strong>
                    </span>
                  </div>
                )}

                <Button
                  className="h-11 w-full rounded-xl bg-primary-400 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-primary-500 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={isLoading || !canResend}
                  type="submit"
                >
                  {isLoading ? (
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Lock className="mr-2 h-4 w-4" />
                  )}
                  {getButtonText()}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  )
}
